import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { analyticsApi } from '../../api/analytics.api';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  MicrophoneIcon,
  ClockIcon,
  DocumentTextIcon,
  LanguageIcon,
  PlayIcon,
  PauseIcon,
  StopIcon
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#3ee851', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

function StatCard({ title, value, subtitle, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

function AudioRow({ audio, index, expanded, onToggle, onPlay, isPlaying, currentlyPlayingIndex }) {
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const isCurrentlyPlaying = isPlaying && currentlyPlayingIndex === index;
  const handlePlayClick = (e) => {
    e.stopPropagation();
    if (audio.audioUrl) {
      onPlay(audio);
    }
  };

  const handleRowClick = (e) => {
    e.preventDefault();
    onToggle();
  };
  const rows = [
    <tr key={`main-${index}`} className="hover:bg-gray-50 cursor-pointer" onClick={handleRowClick}>
      <td className="px-6 py-4 whitespace-nowrap">
        <button className="p-1" type="button">
          {expanded ? (
            <ChevronUpIcon className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
          )}
        </button>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {audio.audioUrl ? (
          <button
            type="button"
            onClick={handlePlayClick}
            className={`p-2 rounded-full transition-colors ${
              isCurrentlyPlaying
                ? 'bg-[#3ee851] text-black'
                : 'bg-gray-100 text-gray-600 hover:bg-[#3ee851] hover:text-black'
            }`}
            title={isCurrentlyPlaying ? 'Playing' : 'Play audio'}
          >
            {isCurrentlyPlaying ? (
              <PauseIcon className="h-4 w-4" />
            ) : (
              <PlayIcon className="h-4 w-4" />
            )}
          </button>
        ) : (
          <span className="text-gray-400 text-xs">No audio</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {audio.createdAt ? new Date(audio.createdAt).toLocaleString() : 'N/A'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <p className="text-sm font-medium text-gray-900">
            {audio.user?.fullName || audio.user?.username || 'Unknown'}
          </p>
          <p className="text-xs text-gray-500">@{audio.user?.username}</p>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {audio.name || 'Unnamed'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDuration(audio.duration)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatSize(audio.size)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <Badge variant="secondary">{audio.recordingLanguage || 'N/A'}</Badge>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {audio.transcriptError ? (
          <Badge variant="danger">Error</Badge>
        ) : audio.transcript ? (
          <Badge variant="success">Transcribed</Badge>
        ) : (
          <Badge variant="warning">Pending</Badge>
        )}
      </td>
    </tr>
  ];

  if (expanded) {
    rows.push(
      <tr key={`expanded-${index}`}>
        <td colSpan={9} className="px-6 py-4 bg-gray-50">
          <div className="space-y-4">
            {/* Audio Player */}
            {audio.audioUrl && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Audio Player</p>
                <audio
                  controls
                  className="w-full"
                  src={audio.audioUrl}
                >
                  Your browser does not support the audio element.
                </audio>
              </div>
            )}
            {audio.transcript && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Transcript</p>
                <div className="bg-white p-3 rounded border text-sm max-h-40 overflow-y-auto">
                  {audio.transcript}
                </div>
              </div>
            )}
            {audio.transcriptError && (
              <div>
                <p className="text-xs font-medium text-red-500 mb-1">Transcription Error</p>
                <div className="bg-red-50 text-red-700 p-3 rounded border border-red-200 text-sm">
                  {audio.transcriptError}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs font-medium text-gray-500">Recording Language</p>
                <p className="text-sm text-gray-900">{audio.recordingLanguage || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">User Language</p>
                <p className="text-sm text-gray-900">{audio.userLanguage || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Extension</p>
                <p className="text-sm text-gray-900">{audio.extension || 'N/A'}</p>
              </div>
              {audio.transcriptionDate && (
                <div>
                  <p className="text-xs font-medium text-gray-500">Transcribed At</p>
                  <p className="text-sm text-gray-900">
                    {new Date(audio.transcriptionDate).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return rows;
}

export default function AudiosPage() {
  const [data, setData] = useState({ audios: [], pagination: {} });
  const [summary, setSummary] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ languages: [] });
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [currentlyPlayingIndex, setCurrentlyPlayingIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef] = useState(() => typeof Audio !== 'undefined' ? new Audio() : null);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    language: '',
    hasTranscript: '',
    startDate: '',
    endDate: '',
    search: ''
  });

  // Audio player event handlers
  useEffect(() => {
    if (!audioRef) return;

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentlyPlayingIndex(null);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    audioRef.addEventListener('ended', handleEnded);
    audioRef.addEventListener('pause', handlePause);
    audioRef.addEventListener('play', handlePlay);

    return () => {
      audioRef.removeEventListener('ended', handleEnded);
      audioRef.removeEventListener('pause', handlePause);
      audioRef.removeEventListener('play', handlePlay);
      audioRef.pause();
    };
  }, [audioRef]);

  const handlePlayAudio = (audio, index) => {
    if (!audioRef) return;

    if (currentlyPlayingIndex === index && isPlaying) {
      // Pause current audio
      audioRef.pause();
      setIsPlaying(false);
    } else {
      // Set the index first before playing to avoid race condition
      setCurrentlyPlayingIndex(index);
      setIsPlaying(true);
      audioRef.src = audio.audioUrl;
      audioRef.play().catch(err => {
        console.error('Error playing audio:', err);
        setIsPlaying(false);
        setCurrentlyPlayingIndex(null);
      });
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [audiosRes, summaryRes] = await Promise.all([
        analyticsApi.getAudios(filters),
        analyticsApi.getAudiosSummary(30)
      ]);
      setData(audiosRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching audios:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await analyticsApi.getAudioFilterOptions();
        setFilterOptions(response.data);
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    setExpandedRows([]);
  };

  const toggleRow = (id) => {
    const values = [...expandedRows];
    const index = values.indexOf(id);
    if (index > -1) {
      values.splice(index, 1);
    } else {
      values.push(id);
    }
    setExpandedRows(values);
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      language: '',
      hasTranscript: '',
      startDate: '',
      endDate: '',
      search: ''
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m ${secs}s`;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  return (
    <DashboardLayout>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audio Transcriptions</h1>
          <p className="text-gray-600">Monitor audio recordings and transcriptions</p>
        </div>
        <Button
          variant="secondary"
          onClick={() => setShowFilters(!showFilters)}
        >
          <FunnelIcon className="h-4 w-4 mr-2" />
          Filters
        </Button>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Audios"
            value={summary.summary?.totalAudios?.toLocaleString() || 0}
            subtitle="Last 30 days"
            icon={MicrophoneIcon}
            color="bg-blue-500"
          />
          <StatCard
            title="Total Duration"
            value={formatDuration(summary.summary?.totalDuration)}
            icon={ClockIcon}
            color="bg-purple-500"
          />
          <StatCard
            title="Transcribed"
            value={summary.summary?.withTranscript?.toLocaleString() || 0}
            subtitle={`${summary.summary?.transcriptionRate || 0}% rate`}
            icon={DocumentTextIcon}
            color="bg-green-500"
          />
          <StatCard
            title="Total Size"
            value={formatSize(summary.summary?.totalSize)}
            icon={LanguageIcon}
            color="bg-orange-500"
          />
        </div>
      )}

      {/* Charts Row */}
      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Daily Audio Trend */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Audio Uploads</h3>
            {summary.dailyAudios?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={summary.dailyAudios}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="_id"
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis />
                  <Tooltip
                    labelFormatter={(label) => new Date(label).toLocaleDateString()}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#3ee851"
                    strokeWidth={2}
                    dot={false}
                    name="Audios"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </div>

          {/* Language Breakdown */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Language Distribution</h3>
            {summary.languageBreakdown?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={summary.languageBreakdown.filter(l => l._id)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ _id, count }) => `${_id}: ${count}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                    nameKey="_id"
                  >
                    {summary.languageBreakdown.filter(l => l._id).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">
                No language data available
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="Search in transcripts..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3ee851] focus:border-transparent"
          />
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Language
              </label>
              <select
                value={filters.language}
                onChange={(e) => handleFilterChange('language', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#3ee851] focus:border-transparent"
              >
                <option value="">All Languages</option>
                {filterOptions.languages.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transcript Status
              </label>
              <select
                value={filters.hasTranscript}
                onChange={(e) => handleFilterChange('hasTranscript', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#3ee851] focus:border-transparent"
              >
                <option value="">All</option>
                <option value="true">Transcribed</option>
                <option value="false">Not Transcribed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#3ee851] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#3ee851] focus:border-transparent"
              />
            </div>
            <div className="flex items-end">
              <Button variant="secondary" onClick={clearFilters} className="w-full">
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3ee851]"></div>
          </div>
        ) : data.audios?.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10"></th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                      Play
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Language
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.audios.flatMap((audio, i) => (
                    <AudioRow
                      key={i}
                      audio={audio}
                      index={i}
                      expanded={expandedRows.includes(i)}
                      onToggle={() => toggleRow(i)}
                      onPlay={() => handlePlayAudio(audio, i)}
                      isPlaying={isPlaying}
                      currentlyPlayingIndex={currentlyPlayingIndex}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing {((data.pagination.page - 1) * data.pagination.limit) + 1} to{' '}
                {Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)} of{' '}
                {data.pagination.total} results
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(data.pagination.page - 1)}
                  disabled={data.pagination.page <= 1}
                  className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <span className="px-4 py-2 text-sm">
                  Page {data.pagination.page} of {data.pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(data.pagination.page + 1)}
                  disabled={data.pagination.page >= data.pagination.pages}
                  className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No audio files found
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
